import React from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import styled from "styled-components";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import { useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import ArrowsShuffleIcon from "@icons/ArrowsShuffleIcon.svg";
import * as UserSettingsService from "@user/service/UserSettingsService";
import * as DragAndDropUtil from "@util/DragAndDropUtil";
import { SelectSpaceEvent } from "../../AmplitudeSpaceEvents";
import SavedSpaceElement from "../VirtualSpaceWidgets/CurrentVirtualSpace/SpaceActions/SavedSpaceElement/SavedSpaceElement";

const NO_SAVED_SPACES_MESSAGE = "Press ♡ to favorite a space";
const SHUFFLE_BUTTON_LABEL = "Shuffle";

const StyledShuffleIcon = styled(ArrowsShuffleIcon)`
    fill: ${(props) => props.theme.backgroundPrimary};
`;

const StyledShuffleButton = styled(LifeAtButtonV2)`
    width: 100%;
    margin: 0 15px;
`;

const SavedSpacesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
`;

const SpaceList = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 10px;
    padding: 15px 0;
`;

export default function FavoriteSpacesList() {
    const { spaceController } = useVirtualSpaceContext();
    const { user } = useUserContext();
    const amplitudeService = AmplitudeService.getInstance();
    const [savedSpaceData, setSavedSpaceData] = React.useState<VirtualSpace[]>(spaceController.savedSpaces);

    const handleSelectSpace = (space: VirtualSpace) => {
        spaceController.playSpace(space);
        amplitudeService.logEvent(
            new SelectSpaceEvent(
                {
                    spaceId: space.id,
                    spaceName: space.name,
                    spaceCategory: space.category,
                    spaceCreator: space.creatorName,
                },
                "favorites",
            ),
        );
    };

    function onDragEnd(result: DropResult): void {
        DragAndDropUtil.handleOnDragEnd(result, savedSpaceData, (updatedData: VirtualSpace[]) => {
            UserSettingsService.updateSavedSpaces(
                updatedData.map((space) => {
                    return space.id;
                }),
                user,
            );
            setSavedSpaceData(updatedData);
        });
    }

    const removeSavedSpace = (target: VirtualSpace) => {
        const isSpaceSaved = spaceController.savedSpaces.filter((currSpace) => currSpace.id === target.id).length > 0;
        spaceController.toggleSpaceSaved(!isSpaceSaved, target);

        const updatedData = savedSpaceData.filter((space: VirtualSpace) => space.id !== target.id);
        UserSettingsService.updateSavedSpaces(
            updatedData.map((space) => {
                return space.id;
            }),
            user,
        );
        setSavedSpaceData(updatedData);
    };

    const savedSpaces = savedSpaceData.map((space: VirtualSpace, idx: number) => {
        return (
            <Draggable
                key={`draggable-saved-space-${space.id}`}
                draggableId={`draggable-saved-space-${space.id}`}
                index={idx}
            >
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <SavedSpaceElement
                            key={`saved-space-${space.id}`}
                            savedSpace={space}
                            switchToSpace={() => handleSelectSpace(space)}
                            unsaveSpace={() => removeSavedSpace(space)}
                            enableReorder={true}
                            isSpaceRemoved={space.removed}
                            exceedsFreeSaveCount={false}
                        />
                    </div>
                )}
            </Draggable>
        );
    });

    return (
        <SpaceList>
            {savedSpaceData.length > 0 ? (
                <>
                    <StyledShuffleButton
                        onClick={() => {
                            const excludingCurrSpc = savedSpaceData.filter(
                                (value) => value.id !== spaceController.selectedSpace.id,
                            );
                            spaceController.playSpace(
                                excludingCurrSpc[Math.floor(Math.random() * excludingCurrSpc.length)],
                            );
                        }}
                        variant={"inverseColor"}
                    >
                        <StyledShuffleIcon />
                        {SHUFFLE_BUTTON_LABEL}
                    </StyledShuffleButton>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId={`saved-spaces-list`}>
                            {(provided) => (
                                <SavedSpacesList
                                    className={`saved-spaces-draggable-div`}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {savedSpaces}
                                </SavedSpacesList>
                            )}
                        </Droppable>
                    </DragDropContext>
                </>
            ) : (
                <LifeAtBodyPrimary>{NO_SAVED_SPACES_MESSAGE}</LifeAtBodyPrimary>
            )}
        </SpaceList>
    );
}
